import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { MapSarJobProgressOverlay } from '@/features/map/components/ui/MapSarJobProgressOverlay';
import { SarFloodAnalysisWizardModal } from '@/features/map/components/ui/SarFloodAnalysisWizardModal';
import {
  getAoiToolbarNudgeDownPx,
  getMapDesktopBottomStackCss,
  getMapMobileToolsRowBottomCss,
  MAP_OVERLAY_Z,
} from '@/features/map/constants/mapOverlayLayout';
import type { MapAnalysisRegionDrawingContextValue } from '@/features/map/context/mapAnalysisRegionDrawingContext.types';
import { MapAnalysisRegionDrawingContext } from '@/features/map/context/mapAnalysisRegionDrawingContextInstance';
import { useMapAnalysisRegionDrawing } from '@/features/map/hooks/useMapAnalysisRegionDrawing';
import { useMapOverlayInsets } from '@/features/map/hooks/useMapOverlayInsets';
import { useAppSelector } from '@/hooks/useAppSelector';

/**
 * Một nguồn state vẽ AOI cho cả bản đồ 2D và 3D (cả hai có thể mount cùng lúc trong dashboard).
 * Wizard SAR và overlay tiến độ job render tại đây để tránh trùng.
 */
export function MapAnalysisRegionDrawingProvider({ children }: { children: ReactNode }) {
  const [sarWizardOpen, setSarWizardOpen] = useState(false);
  const openSarWizard = useCallback(() => setSarWizardOpen(true), []);

  const drawing = useMapAnalysisRegionDrawing({
    onValidPolygonCommitted: () => setSarWizardOpen(true),
  });

  const { clearAll } = drawing;
  const drawnPolygonRing = useAppSelector((state) => state.annotations.drawnPolygonRing);
  const { contentInsetLeft, isMobile } = useMapOverlayInsets();
  const floodLayerVisible = useAppSelector(
    (s) => s.layer.layers.find((l) => l.id === 'flood')?.visible ?? false,
  );
  const aoiToolbarBottomOpts = useMemo(
    () => ({ toolsRowNudgeDownPx: getAoiToolbarNudgeDownPx(floodLayerVisible) }),
    [floodLayerVisible],
  );
  const desktopBottomStack = useMemo(
    () => getMapDesktopBottomStackCss(aoiToolbarBottomOpts),
    [aoiToolbarBottomOpts],
  );
  const mapToolsRowBottom = isMobile
    ? getMapMobileToolsRowBottomCss(aoiToolbarBottomOpts)
    : desktopBottomStack.toolsRowBottom;

  const value = useMemo(
    (): MapAnalysisRegionDrawingContextValue => ({ ...drawing, openSarWizard }),
    [drawing, openSarWizard],
  );

  return (
    <MapAnalysisRegionDrawingContext.Provider value={value}>
      {children}
      <SarFloodAnalysisWizardModal
        open={sarWizardOpen}
        onClose={() => setSarWizardOpen(false)}
        polygonRing={drawnPolygonRing}
        onSubmitted={() => {
          clearAll();
        }}
      />
      <MapSarJobProgressOverlay
        leftOffsetPx={contentInsetLeft}
        bottomPx={`calc(${mapToolsRowBottom} + 56px)`}
        zIndex={MAP_OVERLAY_Z.tooltip}
      />
    </MapAnalysisRegionDrawingContext.Provider>
  );
}
